<template>
  <b-modal
    v-model="showLocal"
    @hidden="close"
    dialog-class="home-section-editor"
    size="lg"
    centered
    no-close-on-backdrop
    hide-footer
    :title="modalTitle"
  >
    <form class="custom-form" @submit.prevent="save" novalidate>
      <!-- Comunes: título y subtítulo (el separador no tiene texto) -->
      <div class="row" v-if="form.type !== types.SEPARATOR.key">
        <div class="col-md-6">
          <div class="form-group">
            <label for="section-title">Título</label>
            <FormInput id="section-title" type="text" maxlength="200" placeholder="Opcional"
              v-model="form.title" />
          </div>
        </div>
        <div class="col-md-6" v-if="form.type !== types.FREE_TEXT.key">
          <div class="form-group">
            <label for="section-subtitle">Subtítulo</label>
            <FormInput id="section-subtitle" type="text" maxlength="500" placeholder="Opcional"
              v-model="form.subtitle" />
          </div>
        </div>
      </div>

      <!-- Modo y columnas: no aplican a texto libre, barra de cupón ni separador -->
      <div class="row" v-if="hasLayoutOptions">
        <div class="col-md-6">
          <div class="form-group">
            <label>Modo</label>
            <div class="d-flex">
              <b-form-radio v-for="mode in displayModes" :key="mode.key" v-model="form.displayMode"
                :value="mode.key" class="mr-4">
                {{ mode.label }}
              </b-form-radio>
            </div>
          </div>
        </div>
        <div class="col-md-6">
          <div class="form-group">
            <label for="section-columns">Columnas</label>
            <FormSelect id="section-columns" v-model="form.columns" :items="columnOptions"
              defaultOption="Elegir columnas" />
          </div>
        </div>
      </div>

      <!-- BANNERS: se crean aquí mismo -->
      <section v-if="form.type === types.BANNERS.key">
        <div class="banner-item" v-for="(banner, index) in banners" :key="banner.uid">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <span class="font-weight-bold">Banner {{ index + 1 }}</span>
            <button type="button" class="border-0 bg-transparent text-danger p-0" @click="removeBanner(index)">
              <i class="ri-delete-bin-line"></i> Quitar
            </button>
          </div>

          <UploadImages :maxFiles="1" :files="banner.images" @onChange="(files) => onBannerImage(banner, files)"
            @remove="() => clearBannerImage(banner)" />

          <div class="row mt-3">
            <div class="col-md-6">
              <div class="form-group">
                <label>Título del banner</label>
                <FormInput type="text" maxlength="200" v-model="banner.title" />
              </div>
            </div>
            <div class="col-md-6">
              <div class="form-group">
                <label>Subtítulo</label>
                <FormInput type="text" maxlength="500" v-model="banner.subtitle" />
              </div>
            </div>
            <div class="col-md-6">
              <div class="form-group">
                <label>Texto del botón</label>
                <FormInput type="text" maxlength="100" placeholder="Ej: Ver más" v-model="banner.buttonText" />
              </div>
            </div>
            <div class="col-md-6">
              <div class="form-group">
                <label>Enlace a</label>
                <FormSelect v-model="banner.linkType" :items="linkTypeOptions" defaultOption="Sin enlace" />
              </div>
            </div>

            <div class="col-12" v-if="banner.linkType === linkTypes.EXTERNAL.key">
              <div class="form-group">
                <label>URL</label>
                <FormInput type="text" maxlength="500" placeholder="https://..." v-model="banner.linkUrl" />
              </div>
            </div>
            <div class="col-12" v-if="banner.linkType === linkTypes.CATEGORY.key">
              <div class="form-group">
                <label>Categoría</label>
                <FormSelect v-model="banner.linkTargetId" :items="categoryOptions" defaultOption="Elegir categoría" />
              </div>
            </div>
            <div class="col-12" v-if="banner.linkType === linkTypes.PAGE.key">
              <div class="form-group">
                <label>Página</label>
                <FormSelect v-model="banner.linkTargetId" :items="pageOptions" defaultOption="Elegir página" />
              </div>
            </div>
            <div class="col-12" v-if="banner.linkType === linkTypes.PRODUCT.key">
              <div class="form-group">
                <label>Producto</label>
                <p class="mb-2" v-if="banner.linkTargetName">
                  <span class="filter-item">{{ banner.linkTargetName }}</span>
                </p>
                <FormInput type="text" placeholder="Buscar por nombre o código" v-model="banner.productSearch"
                  @input="searchBannerProduct(banner)" />
                <ul class="list-unstyled search-results mt-2" v-if="banner.productResults.length">
                  <li v-for="product in banner.productResults" :key="product.id"
                    class="d-flex justify-content-between align-items-center py-1">
                    <span>{{ product.name }} <small class="text-muted">({{ product.code }})</small></span>
                    <button type="button" class="button button-light py-0" @click="setBannerProduct(banner, product)">
                      Elegir
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <b-form-checkbox v-model="banner.openNewTab" v-if="banner.linkType">
            <span class="custom-checkbox-label">Abrir en una pestaña nueva</span>
          </b-form-checkbox>
        </div>

        <p class="mb-3">
          <a href="" class="add-link font-weight-bold" @click.prevent="addBanner()">+ Agregar banner</a>
        </p>
      </section>

      <!-- PRODUCTS: fuente, cantidad y selección -->
      <section v-if="form.type === types.PRODUCTS.key">
        <div class="form-group">
          <label>Fuente</label>
          <div class="d-flex flex-wrap">
            <b-form-radio v-for="source in productSources" :key="source.key" v-model="form.source"
              :value="source.key" class="mr-4 mb-1">
              {{ source.label }}
            </b-form-radio>
          </div>
        </div>

        <div class="form-group" v-if="form.source === sources.CATEGORY.key">
          <label for="section-category">Categoría *</label>
          <FormSelect id="section-category" v-model="form.sourceCategoryId" :items="categoryOptions"
            defaultOption="Elegir categoría" />
        </div>

        <div class="form-group">
          <label for="section-quantity">Cantidad de productos</label>
          <FormInput id="section-quantity" type="number" min="1" max="20" step="1" placeholder="8"
            v-model="form.quantity" />
        </div>

        <div class="form-group" v-if="form.source === sources.MANUAL.key">
          <label>Productos *</label>
          <FormInput type="text" placeholder="Buscar por nombre o código" v-model="productSearch" />
          <ul class="list-unstyled search-results mt-2" v-if="productResults.length">
            <li v-for="product in productResults" :key="product.id"
              class="d-flex justify-content-between align-items-center py-1">
              <span>{{ product.name }} <small class="text-muted">({{ product.code }})</small></span>
              <button type="button" class="button button-light py-0" :disabled="isProductSelected(product.id)"
                @click="addProduct(product)">
                {{ isProductSelected(product.id) ? "Agregado" : "Agregar" }}
              </button>
            </li>
          </ul>
          <!-- Arrastrar para ordenar -->
          <draggable v-model="selectedProducts" class="mt-2 d-flex flex-wrap" handle=".drag-handle">
            <span v-for="product in selectedProducts" :key="product.id" class="filter-item">
              <i class="ri-draggable drag-handle mr-1"></i>
              {{ product.name }}
              <button type="button" class="text-white p-0 bg-transparent border-0 ml-2"
                @click="removeProduct(product.id)">
                <i class="ri-close-circle-line"></i>
              </button>
            </span>
          </draggable>
        </div>
      </section>

      <!-- CATEGORIES y BRANDS: selección múltiple con filtro -->
      <section v-if="isReferenceList">
        <div class="form-group">
          <label>{{ form.type === types.CATEGORIES.key ? "Categorías" : "Marcas" }}</label>
          <FormInput type="text" placeholder="Filtrar por nombre" v-model="optionFilter" />
          <ul class="list-unstyled reference-options mt-2">
            <li v-for="option in filteredReferenceOptions" :key="option.id" :class="{ 'pl-4': option.child }">
              <b-form-checkbox v-model="selectedIds" :value="option.id">
                <span class="custom-checkbox-label">{{ option.name }}</span>
              </b-form-checkbox>
            </li>
          </ul>
          <small class="text-muted">{{ selectedIds.length }} seleccionadas</small>
        </div>
      </section>

      <!-- FREE_TEXT: contenido HTML -->
      <section v-if="form.type === types.FREE_TEXT.key">
        <div class="form-group">
          <label>Contenido</label>
          <global-trumbowyg v-model="form.subtitle" />
        </div>
      </section>

      <b-form-checkbox v-model="form.isVisible" switch class="mb-3">
        <span class="custom-checkbox-label">Visible en la tienda</span>
      </b-form-checkbox>

      <div class="form-group-buttons text-right mb-0">
        <button type="button" class="button button-light" @click="close()">Cancelar</button>
        <button type="submit" class="button button-primary" :disabled="saving">
          {{ saving ? "Guardando..." : "Guardar" }}
        </button>
      </div>
    </form>
  </b-modal>
</template>

<script>
import { Alert, FormInput, FormSelect, UploadImages } from "@wizepe/wize-admin";
import draggable from "vuedraggable";
import { mapGetters } from "vuex";

import GlobalTrumbowyg from "@/core/components/common/GlobalTrumbowyg";
import UploadImagesMixin from "@/core/mixins/UploadImagesMixin";
import { BrandService, HomeSectionService, PostService, ProductService } from "@/core/services";
import { Constants as LocalConstants } from "@/core/utils";

const DEFAULT_QUANTITY = 8;
let uidCounter = 0;

const emptyForm = (type) => ({
  type,
  title: null,
  subtitle: null,
  displayMode: LocalConstants.HomeDisplayModes.STATIC.key,
  columns: 3,
  source: LocalConstants.HomeProductSources.MANUAL.key,
  sourceCategoryId: null,
  quantity: DEFAULT_QUANTITY,
  isVisible: true,
});

// Banner en edición (uid solo para la key del v-for)
const newBanner = (data = {}) => ({
  uid: ++uidCounter,
  imageAssetId: data.imageAsset ? data.imageAsset.id : null,
  images: data.imageAsset ? [data.imageAsset] : [],
  title: data.title || null,
  subtitle: data.subtitle || null,
  buttonText: data.buttonText || null,
  linkType: data.linkType || null,
  linkUrl: data.linkUrl || null,
  linkTargetId: data.linkTargetId || null,
  linkTargetName: null,
  openNewTab: !!data.openNewTab,
  productSearch: "",
  productResults: [],
  searchTimer: null,
});

export default {
  name: "HomeSectionEditor",
  components: {
    FormInput,
    FormSelect,
    UploadImages,
    GlobalTrumbowyg,
    draggable,
  },
  mixins: [UploadImagesMixin],
  props: {
    show: Boolean,
    // Sección a editar; null al crear
    sectionId: [Number, String],
    // Tipo elegido al crear
    initialType: String,
  },
  data() {
    return {
      showLocal: false,
      saving: false,
      types: LocalConstants.HomeSectionTypes,
      sources: LocalConstants.HomeProductSources,
      linkTypes: LocalConstants.HomeLinkTypes,
      displayModes: Object.values(LocalConstants.HomeDisplayModes),
      productSources: Object.values(LocalConstants.HomeProductSources),
      form: emptyForm(null),
      banners: [],
      selectedProducts: [],
      selectedIds: [],
      productSearch: "",
      productResults: [],
      productSearchTimer: null,
      optionFilter: "",
      brands: [],
      pages: [],
    };
  },
  computed: {
    ...mapGetters({
      categories: "app/getCategories",
    }),
    modalTitle() {
      const type = this.types[this.form.type];
      const label = type ? type.label : "Sección";
      return this.sectionId ? `Editar sección: ${label}` : `Nueva sección: ${label}`;
    },
    // Productos: hasta 5 por fila (como la cuadrícula de categoría en la tienda); el resto, hasta 4
    columnOptions() {
      const values = this.form.type === this.types.PRODUCTS.key ? [2, 3, 4, 5] : [1, 2, 3, 4];
      return values.map((value) => ({ key: value, label: `${value}` }));
    },
    hasLayoutOptions() {
      return ![this.types.SEPARATOR.key, this.types.FREE_TEXT.key, this.types.COUPON_BAR.key]
        .includes(this.form.type);
    },
    isReferenceList() {
      return [this.types.CATEGORIES.key, this.types.BRANDS.key].includes(this.form.type);
    },
    linkTypeOptions() {
      return Object.values(this.linkTypes).map((item) => ({ key: item.key, label: item.label }));
    },
    // Categorías padre y debajo sus hijas
    categoryList() {
      const parents = this.categories.filter((c) => !c.parent);
      return parents.reduce((acc, parent) => {
        acc.push({ id: parent.id, name: parent.name, child: false });
        this.categories
          .filter((c) => c.parent && c.parent.id === parent.id)
          .forEach((c) => acc.push({ id: c.id, name: c.name, child: true }));
        return acc;
      }, []);
    },
    categoryOptions() {
      return this.categoryList.map((c) => ({ key: c.id, label: c.child ? `— ${c.name}` : c.name }));
    },
    pageOptions() {
      return this.pages.map((page) => ({ key: page.id, label: page.name }));
    },
    filteredReferenceOptions() {
      const options = this.form.type === this.types.CATEGORIES.key
        ? this.categoryList
        : this.brands.map((b) => ({ id: b.id, name: b.name, child: false }));
      const term = (this.optionFilter || "").toLowerCase().trim();
      return term ? options.filter((o) => o.name.toLowerCase().includes(term)) : options;
    },
  },
  watch: {
    show(value) {
      this.showLocal = value;
      if (value) this.open();
    },
    productSearch(value) {
      clearTimeout(this.productSearchTimer);
      this.productSearchTimer = setTimeout(() => this.searchProducts(value), 400);
    },
  },
  methods: {
    async open() {
      this.form = emptyForm(this.initialType);
      this.banners = [];
      this.selectedProducts = [];
      this.selectedIds = [];
      this.productSearch = "";
      this.productResults = [];
      this.optionFilter = "";

      await Promise.all([this.$store.dispatch("app/loadCategories"), this.loadBrands(), this.loadPages()]);
      if (this.sectionId) await this.load();
      if (this.form.type === this.types.BANNERS.key && !this.banners.length) this.addBanner();
    },
    async load() {
      try {
        this.$store.dispatch("app/loading", true);
        const response = await HomeSectionService.get(this.sectionId);
        const section = response.payload;

        this.form = {
          type: section.type,
          title: section.title,
          subtitle: section.subtitle,
          displayMode: section.displayMode || this.displayModes[0].key,
          columns: section.columns || 3,
          source: section.source || this.sources.MANUAL.key,
          sourceCategoryId: section.sourceCategoryId,
          quantity: section.quantity || DEFAULT_QUANTITY,
          isVisible: section.isVisible !== false,
        };

        const items = section.items || [];
        if (section.type === this.types.BANNERS.key) {
          this.banners = items.map((item) => newBanner(item));
        } else if (section.type === this.types.PRODUCTS.key) {
          this.selectedProducts = items
            .filter((item) => item.referenceId)
            .map((item) => ({ id: item.referenceId, name: item.referenceName || `Producto #${item.referenceId}` }));
        } else if (this.isReferenceList) {
          this.selectedIds = items.filter((item) => item.referenceId).map((item) => item.referenceId);
        }
      } catch (ex) {
        console.error(ex);
        Alert.error(ex.errors ? ex.errors.message : ex);
      } finally {
        this.$store.dispatch("app/loading", false);
      }
    },
    async loadBrands() {
      if (this.brands.length) return;
      try {
        const response = await BrandService.listActive();
        this.brands = response.payload || [];
      } catch (error) {
        console.error(error);
      }
    },
    async loadPages() {
      if (this.pages.length) return;
      try {
        const response = await PostService.list({ page: 0, size: 100 });
        this.pages = response.payload.content || [];
      } catch (error) {
        console.error(error);
      }
    },
    // --- Banners ---
    addBanner() {
      this.banners.push(newBanner());
    },
    removeBanner(index) {
      this.banners.splice(index, 1);
    },
    async onBannerImage(banner, files) {
      if (!files.length) return;
      const uploaded = await this.uploadFiles(files);
      if (uploaded && uploaded.length) {
        banner.imageAssetId = uploaded[0].id;
        banner.images = [uploaded[0]];
      }
    },
    clearBannerImage(banner) {
      banner.imageAssetId = null;
      banner.images = [];
    },
    searchBannerProduct(banner) {
      clearTimeout(banner.searchTimer);
      banner.searchTimer = setTimeout(async () => {
        banner.productResults = await this.findProducts(banner.productSearch);
      }, 400);
    },
    setBannerProduct(banner, product) {
      banner.linkTargetId = product.id;
      banner.linkTargetName = product.name;
      banner.productSearch = "";
      banner.productResults = [];
    },
    // --- Productos elegidos a mano ---
    async searchProducts(term) {
      this.productResults = await this.findProducts(term);
    },
    async findProducts(term) {
      if (!term || term.trim().length < 2) return [];
      try {
        const response = await ProductService.list({ search: term.trim(), page: 0, size: 10 });
        return response.payload.content || [];
      } catch (error) {
        console.error(error);
        return [];
      }
    },
    isProductSelected(id) {
      return this.selectedProducts.some((product) => product.id === id);
    },
    addProduct(product) {
      if (this.isProductSelected(product.id)) return;
      this.selectedProducts.push({ id: product.id, name: product.name });
    },
    removeProduct(id) {
      this.selectedProducts = this.selectedProducts.filter((product) => product.id !== id);
    },
    // --- Guardar ---
    buildItems() {
      const type = this.form.type;
      if (type === this.types.BANNERS.key) {
        return this.banners.map((banner, index) => ({
          imageAssetId: banner.imageAssetId,
          title: banner.title,
          subtitle: banner.subtitle,
          buttonText: banner.buttonText,
          linkType: banner.linkType || null,
          linkUrl: banner.linkType === this.linkTypes.EXTERNAL.key ? banner.linkUrl : null,
          linkTargetId: banner.linkType && banner.linkType !== this.linkTypes.EXTERNAL.key
            ? banner.linkTargetId : null,
          openNewTab: !!banner.openNewTab,
          position: index + 1,
        }));
      }
      if (type === this.types.PRODUCTS.key && this.form.source === this.sources.MANUAL.key) {
        return this.selectedProducts.map((product, index) => ({ referenceId: product.id, position: index + 1 }));
      }
      if (this.isReferenceList) {
        return this.selectedIds.map((id, index) => ({ referenceId: id, position: index + 1 }));
      }
      return [];
    },
    validate() {
      if (this.form.type === this.types.BANNERS.key) {
        if (!this.banners.length) return "Agregue al menos un banner";
        if (this.banners.some((banner) => !banner.imageAssetId)) return "Cada banner necesita una imagen";
      }
      if (this.form.type === this.types.PRODUCTS.key) {
        if (this.form.source === this.sources.MANUAL.key && !this.selectedProducts.length) {
          return "Seleccione al menos un producto";
        }
        if (this.form.source === this.sources.CATEGORY.key && !this.form.sourceCategoryId) {
          return "Seleccione la categoría";
        }
      }
      if (this.isReferenceList && !this.selectedIds.length) {
        return "Seleccione al menos un elemento";
      }
      return null;
    },
    async save() {
      const error = this.validate();
      if (error) {
        Alert.error(error);
        return;
      }

      const isProducts = this.form.type === this.types.PRODUCTS.key;
      const quantity = parseInt(this.form.quantity, 10);
      const payload = {
        ...this.form,
        columns: this.form.columns ? Number(this.form.columns) : null,
        displayMode: this.hasLayoutOptions ? this.form.displayMode : null,
        source: isProducts ? this.form.source : null,
        sourceCategoryId: isProducts && this.form.source === this.sources.CATEGORY.key
          ? this.form.sourceCategoryId : null,
        quantity: isProducts && !Number.isNaN(quantity) ? Math.min(Math.max(quantity, 1), 20) : null,
        items: this.buildItems(),
      };

      try {
        this.saving = true;
        const response = this.sectionId
          ? await HomeSectionService.update(this.sectionId, payload)
          : await HomeSectionService.create(payload);

        // Los errores de validación del backend llegan con HTTP 200 y status BAD_REQUEST
        if (response.status !== "OK") {
          Alert.error("Revise los datos de la sección");
          return;
        }
        Alert.success("Sección guardada");
        this.$emit("saved", response.payload);
        this.close();
      } catch (ex) {
        console.error(ex);
        Alert.error(ex.errors ? ex.errors.message : ex);
      } finally {
        this.saving = false;
      }
    },
    close() {
      this.showLocal = false;
      this.$emit("hide");
    },
  },
};
</script>

<style lang="stylus" scoped>
@import "~@wizepe/wize-admin/dist/styles/variables"

.banner-item
  border: 1px solid #e3e5ea
  border-radius: 8px
  padding: 16px
  margin-bottom: 16px

.search-results,
.reference-options
  max-height: 220px
  overflow-y: auto
  border: 1px solid #e8eaed
  border-radius: 6px
  padding: 4px 10px

.drag-handle
  cursor: grab

.add-link
  color: color-light-grey

.custom-checkbox-label
  font-size: 13px !important
</style>
